import { ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';

export interface IEMediaCommand {
	name?: string;
	time?: number;
	frame?: number;
}

@Injectable()
export class InteractivityMediaService {

	private _fps: number;
	private _currentTime: number;
	private _duration: number;

	private _volume: number;
	private _playing: boolean;

	private _mutedSubject: ReplaySubject<boolean>;
	private _endedSubject: ReplaySubject<boolean>;
	private _volumeSubject: ReplaySubject<number>;
	private _commandSubject: ReplaySubject<IEMediaCommand>;
	private _currentTimeSubject: ReplaySubject<number>;
	private _durationSubject: ReplaySubject<number>;

	constructor(private localStorageService: LocalStorageService) {
		this._currentTime = 0;
		this._duration = 0;
		this._fps = 24;
		this._mutedSubject = new ReplaySubject<boolean>(1);
		this._endedSubject = new ReplaySubject<boolean>(1);
		this._volumeSubject = new ReplaySubject<number>(1);
		this._commandSubject = new ReplaySubject<IEMediaCommand>(1);
		this._currentTimeSubject = new ReplaySubject<number>(1);
		this._currentTimeSubject.next(this._currentTime);
		this._durationSubject = new ReplaySubject<number>(1);
		const volume: number = this.localStorageService.get('interactivity-volume');
		if (typeof volume === 'number') {
			this._volume = volume;
		}
	}

	get endedSubject() {
		return this._endedSubject;
	}

	set ended(value: boolean) {
		this._endedSubject.next(value);
	}

	get mutedSubject(): ReplaySubject<boolean> {
		return this._mutedSubject;
	}

	set muted(muted: boolean) {
		this._mutedSubject.next(muted);
	}

	get volumeSubject(): ReplaySubject<number> {
		return this._volumeSubject;
	}

	set volume(volume: number) {
		if (volume > 1) {
			volume = 1;
		}
		this._volume = volume;
		this._volumeSubject.next(this._volume);
		this.localStorageService.set('interactivity-volume', this._volume);
		if (this._volume === 0) {
			this._mutedSubject.next(true);
		} else {
			this._mutedSubject.next(false);
		}
	}

	get volume(): number {
		return this._volume;
	}

	get commandSubject(): ReplaySubject<IEMediaCommand> {
		return this._commandSubject;
	}
	get currentTimeSubject(): ReplaySubject<any> {
		return this._currentTimeSubject;
	}
	get durationSubject(): ReplaySubject<number> {
		return this._durationSubject;
	}

	get fps(): number {
		return this._fps;
	}
	set fps(fps: number) {
		this._fps = fps;
	}

	get currentTime(): number {
		return this._currentTime;
	}
	set currentTime(t: number) {
		this._currentTime = t;
		this._currentTimeSubject.next(this._currentTime);
	}

	get duration(): number {
		return this._duration;
	}
	set duration(d: number) {
		// console.log( ' - media-service: set duration: ' + d );
		this._duration = d;
		this._durationSubject.next(this._duration);
	}

	get playing(): boolean {
		return this._playing;
	}


	public toggleMute() {
		this._commandSubject.next({
			name: 'toggleMute'
		});
	}

	public togglePlayPause(): void {
		this._commandSubject.next({
			name: 'togglePlay'
		});
	}

	public play(): void {
		// console.log( ' - media-service: play' );
		this._commandSubject.next({
			name: 'play'
		});
	}

	public pause(): void {
		// console.log( ' - media-service: pause' );
		this._commandSubject.next({
			name: 'pause'
		});
	}

	public previousSection(): void {
		this._commandSubject.next({
			name: 'previousSection'
		});
	}

	public previousFrame(): void {
		this._commandSubject.next({
			name: 'previousFrame'
		});
	}

	public nextFrame(): void {
		this._commandSubject.next({
			name: 'nextFrame'
		});
	}

	public nextSection(): void {
		this._commandSubject.next({
			name: 'nextSection'
		});
	}

	public seekToRelativeTime(relativeTime: number): void { // time in ms
		let time = this._currentTime + relativeTime;
		if (time < 0) {
			time = 0;
		}
		this._commandSubject.next({
			name: 'seekToTime',
			time
		});
	}

	public seekToTime(time: number): void { // time in ms
		// console.log( ' - media-service: seekToTime: ' + time );
		this._commandSubject.next({
			name: 'seekToTime',
			time
		});
	}

	public seekToFrame(frame: number): void { // see if we can use frame instead of time?
		this._commandSubject.next({
			name: 'seekToFrame',
			frame
		});
	}

}
